import React, { useState, useEffect, useCallback, useRef } from 'react';
import useSettingsStore from '../store/settingsStore';
import { Layout, Menu, Avatar, Dropdown, Button, theme, Badge, Popover, List, Typography, Tooltip, Space } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  BarChartOutlined,
  BellOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  BankOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckOutlined,
  FileTextOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  CarryOutOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  WalletOutlined,
  SendOutlined,
  FileProtectOutlined,
  UploadOutlined,
  SwapOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import dayjs from 'dayjs';
import useAuthStore from '../store/authStore';
import { logout } from '../api/auth';
import api from '../api/axios';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from '../api/notifications';
import KvkkConsentModal from '../components/common/KvkkConsentModal';
import ChatWidget from '../components/chat/ChatWidget';
import { playNotificationSound } from '../utils/notificationSound';
import { requestNotificationPermission, showBrowserNotifications } from '../utils/browserNotifications';
import BildirimIzniDugmesi from '../components/common/BildirimIzniDugmesi';
import UygulamayiYukleDugmesi from '../components/common/UygulamayiYukleDugmesi';
import { notificationTarget } from '../utils/notificationTarget';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const { appName, logoUrl, vapidPublicKey, fetchSettings } = useSettingsStore();
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { requestNotificationPermission(); }, []);
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout: storeLogout, isImpersonating, impersonationId, originalUser, stopImpersonation } = useAuthStore();
  const prevUnreadCountRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      const newCount = res.data.unread_count || 0;
      // Sayfa ilk acildiginda zaten var olan okunmamis bildirimler icin degil,
      // sadece sonradan artan (yeni gelen) bildirim icin bir kere calsin.
      if (prevUnreadCountRef.current !== null && newCount > prevUnreadCountRef.current) {
        playNotificationSound();
        const delta = newCount - prevUnreadCountRef.current;
        try {
          const latest = await getNotifications({ per_page: Math.min(delta, 5) });
          showBrowserNotifications(latest.data.data || [], logoUrl);
        } catch {}
      }
      prevUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {}
  }, [logoUrl]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleNotifOpen = async (open) => {
    setNotifOpen(open);
    if (open) {
      try {
        const res = await getNotifications({ per_page: 5 });
        setNotifications(res.data.data || []);
        await fetchUnreadCount();
      } catch {}
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {}
  };

  const handleStopImpersonation = async () => {
    try {
      const res = await api.delete(`/admin/impersonate/${impersonationId}`);
      stopImpersonation(res.data.user);
      navigate('/users');
    } catch (err) {
      console.error('Taklit sonlandırma hatası:', err?.response?.data);
      // Backend'den hata gelirse bile frontend state'ini ve cookie'yi zorunlu temizle
      // (backend zaten original_token ile auth_token'ı restore etmişti veya edemedi)
      try { await api.post('/auth/logout'); } catch {}
      stopImpersonation(originalUser);
      navigate('/login');
    }
  };
  const { token } = theme.useToken();

  const handleLogout = async () => {
    if (isImpersonating) {
      await handleStopImpersonation();
      return;
    }
    try {
      await logout();
    } catch (error) {
      // Token süresi dolmuş olabilir
    } finally {
      storeLogout();
      navigate('/login');
    }
  };

  const isIntern = user?.role === 'stajyer';

  // Menü grubu kur: rolü uymayan kalemleri ele, grup tamamen boş kalırsa
  // başlığı da gösterme (ör. öğretmende "Yönetim" grubu hiç görünmez).
  // Gruplar yalnızca görsel — açılır/kapanır değil.
  // Kişi bazında verilen izinler. Rol yetmediğinde buraya bakılıyor: bir
  // öğretmene Kullanıcılar > Yetkiler'den competitions.view verildiğinde
  // menü ROLE göre gizlendiği için bağlantı hiç çıkmıyordu - yetki
  // veriliyor ama kullanılamıyordu.
  const permissions = user?.permissions || [];
  const has = (perm) => permissions.includes(perm);

  const buildGroup = (label, items) => {
    const children = items.filter((item) => {
      if (item.permission && has(item.permission)) return true;
      return !item.roles || item.roles.includes(user?.role);
    });
    return children.length > 0 ? [{ type: 'group', label, children }] : [];
  };

  const STAFF   = ['developer_admin', 'super_admin', 'branch_manager', 'teacher'];
  const MANAGER = ['developer_admin', 'super_admin', 'branch_manager'];
  const ADMIN   = ['developer_admin', 'super_admin'];

  const menuItems = isIntern ? [
    {
      key: '/intern/today',
      icon: <CalendarOutlined />,
      label: 'Bugünkü Program',
    },
    {
      key: '/intern/media',
      icon: <UploadOutlined />,
      label: 'Medya Yükle',
    },
    {
      key: '/intern/announcements',
      icon: <BellOutlined />,
      label: 'Duyurular',
    },
    {
      key: '/intern/agenda',
      icon: <ScheduleOutlined />,
      label: 'Ajanda',
    },
    {
      key: '/intern/work-tasks',
      icon: <CarryOutOutlined />,
      label: 'İş Takip',
    },
    {
      key: '/intern/routines',
      icon: <ClockCircleOutlined />,
      label: 'Rutin',
    },
    {
      key: '/intern/my-days',
      icon: <DollarOutlined />,
      label: 'Çalışma Günlerim',
    },
  ] : [
    // Gruplama ve sıralama 2026-07-29'da kullanıcının verdiği düzene göre
    // yeniden kuruldu. Rutin modülü 2026-07-30'da İş Takip'in altına girdi.
    ...buildGroup('Genel', [
      { key: '/dashboard',   icon: <DashboardOutlined />,   label: 'Dashboard' },
      { key: '/agenda',      icon: <ScheduleOutlined />,    label: 'Ajanda' },
      { key: '/work-tasks',  icon: <CarryOutOutlined />,    label: 'İş Takip' },
      { key: '/routines',    icon: <ClockCircleOutlined />, label: 'Rutin' },
      { key: '/calendar',    icon: <CalendarOutlined />,  label: 'Takvim',        roles: STAFF },
      { key: '/daily-notes', icon: <FileTextOutlined />,  label: 'Günlük Notlar', roles: STAFF },
      { key: '/payments',    icon: <DollarOutlined />,    label: 'Ödemeler',      roles: MANAGER, permission: 'payments.view.all' },
      // Anaokulu Takip - anaokullarina verilen bran$ destegi. KURUMUN NORMAL
      // ISLEYISIYLE HICBIR BAGI YOK (kullanicinin acik talimati): ogrenciler,
      // odemeler ve sertifikalar ayri tablolarda, Finans raporlarina karismaz.
      // Konum kullanicinin istegi: Odemeler ile Odeme Cizelgesi arasi.
      { key: '/preschools',   icon: <HomeOutlined />,      label: 'Anaokulu Takip', roles: ADMIN, permission: 'preschools.view' },
      // Ödeme Çizelgesi bilerek BURADA, Şubeler > şube detayı sekmelerinde
      // değil. Oradaki sekmeler yapılandırma (derslik, dönem, program) — bir
      // kez kurulur. Çizelge ise operasyonel: ay boyunca defalarca açılıp
      // "bunu ödedim" işaretleniyor, üç tık derine gömülmemeli.
      { key: '/expense-schedule', icon: <ScheduleOutlined />, label: 'Ödeme Çizelgesi', roles: MANAGER, permission: 'expense_schedules.view' },
    ]),
    ...buildGroup('Öğrenci ve Eğitim', [
      { key: '/students',               icon: <TeamOutlined />,              label: 'Öğrenciler',       roles: STAFF },
      { key: '/certificate-deliveries', icon: <SafetyCertificateOutlined />, label: 'Sertifika Teslim', roles: STAFF },
      { key: '/parent-requests',        icon: <WarningOutlined />,           label: 'Veli Talepleri',   roles: STAFF },
      // Yarışma modülü normal ders/ödeme akışından tamamen ayrı bir süreç.
      // Varsayılan olarak yalnızca süper admin+; şube müdürüne gerekirse
      // Kullanıcılar > Yetkiler'den kişi bazında verilir (menü rol bazlı
      // gizler ama asıl kısıt backend'deki competitions.* izinlerinde).
      // Varsayılanda yalnızca süper adminde; kişi bazında competitions.view
      // verilen personel de görebilsin diye ayrıca izne bakılıyor.
      { key: '/competitions',           icon: <TrophyOutlined />,            label: 'Yarışmalar',       roles: ADMIN, permission: 'competitions.view' },
      /*
        BANKODA YARIŞMA ÖDEMESİ — ayrı menü kalemi, ayrı ve DAR izin.

        `competitions.view` DEĞİL `competitions.payments`: öğretmen
        yarışma modülünü göremiyor (etap, takım, ücret kalemleri) ama
        bankoda tahsilat alabilmeli. İki kalem aynı anahtarı paylaşsaydı
        tahsilat yetkisi vermek tüm modülü açardı.

        `roles: ADMIN` + izin: `buildGroup` izni rolün yerine geçen bir
        KAÇIŞ KAPISI sayıyor, yani izni verilen öğretmen de görüyor.
        Rota kapısı App.js'te AYNI anahtarla — ikisi birden olmazsa
        yarısı çalışır (frontend/CLAUDE.md).
      */
      { key: '/competition-payments',   icon: <WalletOutlined />,            label: 'Yarışma Ödemeleri', roles: ADMIN, permission: 'competitions.payments' },
    ]),
    ...buildGroup('Yönetim', [
      { key: '/reports',             icon: <BarChartOutlined />,          label: 'Raporlar',          roles: MANAGER, permission: 'reports.view' },
      // Anomali Raporu: sistemin kendi kurallarıyla çelişen kayıtlar.
      // Raporlar'ın hemen altında — ikisi de "veriye bak" işi.
      { key: '/anomalies',           icon: <WarningOutlined />,           label: 'Anomali Raporu',    roles: MANAGER, permission: 'anomalies.view' },
      // NOT users.view DEGIL: ogretmende de users.view/users.create var
      // (veli hesabi olusturabilsin diye). Sube muduru ile ogretmeni ayiran
      // anahtar users.edit - menuyu ona bagladik, yoksa Kullanicilar sayfasi
      // butun ogretmenlere acilirdi.
      { key: '/users',               icon: <UserOutlined />,              label: 'Kullanıcılar',      roles: MANAGER, permission: 'users.edit' },
      { key: '/leave-requests',      icon: <CalendarOutlined />,          label: 'İzin Talepleri',    roles: MANAGER, permission: 'leaves.view' },
      // Profil > "Vekaletlerim" YALNIZCA kisinin kendi kayitlari; buradaki
      // ekran subenin tamami ve baskasi adina vekalet acma icin.
      // permission YAZILMIYOR: delegations.view ogretmende de var ve
      // buildGroup izni rolun yerine gecen bir kacis kapisi sayiyor -
      // yazilsaydi menu butun ogretmenlere cikardi (bkz. Kullanicilar notu).
      { key: '/delegations',         icon: <SwapOutlined />,              label: 'Vekaletler',        roles: MANAGER },
      // Öğretmen de görür (gün onayı için); ücret/bakiye sekmesi sayfanın
      // kendi içinde role göre gizleniyor.
      { key: '/interns',             icon: <DollarOutlined />,            label: 'Stajyer Takibi',    roles: STAFF },
      { key: '/student-badge-types', icon: <SafetyCertificateOutlined />, label: 'Öğrenci Rozetleri', roles: MANAGER, permission: 'student_badge_types.manage' },
      { key: '/branches',            icon: <BankOutlined />,              label: 'Şubeler',           roles: MANAGER, permission: 'branches.view' },
      // Okul katalogu: ogrenci formundaki okul alani artik serbest metin
      // degil bu listeden secim. Ogretmen listeyi gorur (secim kutusu icin
      // gerekli) ama ekleyemez - ekleme schools.manage iznine bagli.
      // Subeler'in yaninda duruyor: ikisi de kurum katalogu (2026-08-07,
      // kullanicinin istegi - once "Ogrenci ve Egitim" altindaydi).
      { key: '/schools',             icon: <BankOutlined />,              label: 'Okullar',           roles: STAFF },
      { key: '/settings',            icon: <SettingOutlined />,           label: 'Ayarlar',           roles: ADMIN },
    ]),
    ...buildGroup('İletişim', [
      { key: '/announcements', icon: <BellOutlined />,        label: 'Duyurular' },
      { key: '/notifications', icon: <SendOutlined />,        label: 'Bildirimler',    roles: MANAGER, permission: 'announcements.create' },
      { key: '/consent-texts', icon: <FileProtectOutlined />, label: 'Onay Metinleri', roles: MANAGER, permission: 'kvkk.view' },
    ]),
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
      onClick: () => navigate(user?.role === 'stajyer' ? '/intern/profile' : '/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış Yap',
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <KvkkConsentModal />
      <ChatWidget />
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: token.colorBgContainer,
          borderRight: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div className="flex items-center justify-center p-4 border-b">
          {!collapsed && (
            <span className="font-bold text-lg text-blue-600">
              {appName}
            </span>
          )}
          {collapsed && (
            <span className="font-bold text-lg text-blue-600">{appName.split(' ').map(w => w[0]).join('').toUpperCase()}</span>
          )}
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname.startsWith('/branches/') ? '/branches' : location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            padding: '0 16px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />

          <div className="flex items-center gap-4">
            <Popover
              trigger="click"
              open={notifOpen}
              onOpenChange={handleNotifOpen}
              placement="bottomRight"
              title={
                <div className="flex justify-between items-center" style={{ minWidth: 300 }}>
                  <span>Bildirimler</span>
                  {unreadCount > 0 && (
                    <Button size="small" type="link" icon={<CheckOutlined />} onClick={handleMarkAllAsRead}>
                      Tümünü Okundu İşaretle
                    </Button>
                  )}
                </div>
              }
              content={
                <div style={{ width: 340 }}>
                  {notifications.length === 0 ? (
                    <p className="text-gray-400 text-center py-4">Bildirim yok.</p>
                  ) : (
                    <List
                      dataSource={notifications}
                      renderItem={(n) => (
                        <List.Item
                          style={{ background: n.read_at ? 'transparent' : '#f0f7ff', cursor: notificationTarget(n, user?.role) ? 'pointer' : 'default', padding: '8px 12px', borderRadius: 4 }}
                          onClick={() => {
                            if (!n.read_at) handleMarkAsRead(n.id);
                            const target = notificationTarget(n, user?.role);
                            if (target) navigate(target);
                          }}
                          actions={[
                            !n.read_at && (
                              <Tooltip title="Okundu işaretle" key="read">
                                <CheckOutlined
                                  style={{ color: '#1677ff' }}
                                  onClick={(e) => { e.stopPropagation(); handleMarkAsRead(n.id); }}
                                />
                              </Tooltip>
                            ),
                          ].filter(Boolean)}
                        >
                          <List.Item.Meta
                            title={<span style={{ fontWeight: n.read_at ? 400 : 600, fontSize: 13 }}>{n.title}</span>}
                            description={
                              <div>
                                <div style={{ fontSize: 12, color: '#555' }}>{n.body}</div>
                                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                                  {dayjs(n.created_at).format('DD.MM.YYYY HH:mm')}
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4,
                  }}>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => { setNotifOpen(false); navigate('/profile?tab=notifications'); }}
                    >
                      Tümünü Gör
                    </Button>
                    {/* Yöneticinin masaüstünde de bu kanala ihtiyacı var:
                        güvenlik uyarısı ve cron arızası tarayıcı kapalıyken
                        ancak buradan ulaşıyor. Başlıktan kaldırıldı, buraya
                        taşındı (Eren Bey, 22.09.2026 — "hoş durmuyor"). */}
                    <Space size={4}>
                      <UygulamayiYukleDugmesi />
                      <BildirimIzniDugmesi vapidPublicKey={vapidPublicKey} rol={user?.role} gorunum="link" />
                    </Space>
                  </div>
                </div>
              }
            >
              <Badge count={unreadCount} size="small">
                <Button type="text" icon={<BellOutlined />} shape="circle" />
              </Badge>
            </Popover>

            {/* PERSONEL BAŞLIĞINDA BİLDİRİM DÜĞMESİ YOK — yeri bildirim
                menüsünün altı (aşağıdaki Popover).
                Veli ekranında telefonda başlıkta da çıkıyor çünkü orada
                ~530 kişinin özelliği kendiliğinden bulması gerekiyor.
                Burada ise kullanıcı birkaç yönetici ve bu başlık telefonda
                zaten kalabalık (menü veli ekranındaki gibi gizlenmiyor);
                bir düğme daha eklemek taşmaya yol açıyordu. */}

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="flex items-center gap-2 cursor-pointer">
                <Avatar icon={<UserOutlined />} />
                <span>{user?.name} {user?.surname}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {isImpersonating && (
          <div style={{ background: '#fff3cd', borderBottom: '1px solid #ffc107', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#856404', fontWeight: 500 }}>
              Taklit modu: <strong>{user?.name} {user?.surname}</strong> olarak görüntülüyorsunuz.
              Gerçek hesap: {originalUser?.name} {originalUser?.surname}
            </span>
            <Button size="small" danger onClick={handleStopImpersonation}>
              Takliti Sonlandır
            </Button>
          </div>
        )}

        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;